import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
// import { Button } from "@/components/ui/button";
import { Button, Badge } from "@musa.tech/design-system";
import { Input } from "@/components/ui/input";
// import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Edit,
  Plus,
  Trash2,
  Save,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import { openCloudHumansChat } from "@/utils/cloudHumans";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthContext } from "@/components/AuthProvider";
import {
  usePoliticasContent,
  PoliticaContent,
  PoliticaContentType,
} from "@/hooks/usePoliticasContent";
import { EditPerguntaFrequenteDialog } from "@/components/politicas/EditPerguntaFrequenteDialog";
import { AddPerguntaFrequenteDialog } from "@/components/politicas/AddPerguntaFrequenteDialog";
import { FAQReadModal } from "@/components/politicas/FAQReadModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { generateSlug, findBySlug } from "@/utils/slugUtils";

const FAQ_BADGE_CLASSES =
  "bg-muted/60 text-muted-foreground border-0 text-[10px] px-2 py-0.5 font-medium uppercase tracking-wide";

const PerguntasFrequentes = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { profile } = useAuthContext();
  const { politicas, isLoading, savePoliticas, deletePolitica, isSaving } =
    usePoliticasContent();

  const [isEditing, setIsEditing] = useState(false);
  const [editingPolitica, setEditingPolitica] =
    useState<PoliticaContent | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [localPoliticas, setLocalPoliticas] = useState<PoliticaContent[]>([]);
  const [originalIds, setOriginalIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<string>("importance");
  const [currentPage, setCurrentPage] = useState(1);
  const [readingPolitica, setReadingPolitica] =
    useState<PoliticaContent | null>(null);

  const ITEMS_PER_PAGE = 6;

  const canEditFAQ = profile?.is_internal === true;

  // Get unique types from politicas
  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    localPoliticas.forEach((politica) => {
      if (politica.type) {
        types.add(politica.type);
      }
    });
    return Array.from(types).sort();
  }, [localPoliticas]);

  // Filter and sort politicas
  const filteredPoliticas = useMemo(() => {
    let filtered = localPoliticas;

    // Filter by type
    if (selectedType !== "all") {
      filtered = filtered.filter((politica) => politica.type === selectedType);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((politica) => {
        const titleMatch = politica.title.toLowerCase().includes(query);
        const plainContent = politica.content.html
          .replace(/<[^>]*>/g, "")
          .toLowerCase();
        const contentMatch = plainContent.includes(query);
        return titleMatch || contentMatch;
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortOrder) {
        case "recent":
          return b.order_index - a.order_index; // Higher index = more recent
        case "importance":
        default:
          return a.order_index - b.order_index; // order_index defines importance
      }
    });

    return sorted;
  }, [localPoliticas, searchQuery, selectedType, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredPoliticas.length / ITEMS_PER_PAGE);
  const paginatedPoliticas = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPoliticas.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredPoliticas, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedType, sortOrder]);

  // Update local politicas when data changes
  useEffect(() => {
    if (politicas && !isEditing) {
      // All users (including internal) only see visible items in view mode
      // Internal users see all (including hidden) only in edit mode
      setLocalPoliticas(politicas.filter((p) => p.is_visible));
    }
  }, [politicas, isEditing, canEditFAQ]);

  // Handle URL-based FAQ opening
  useEffect(() => {
    if (slug && localPoliticas.length > 0) {
      const faq = findBySlug(localPoliticas, slug);
      if (faq) {
        setReadingPolitica(faq);
      }
    }
  }, [slug, localPoliticas]);

  const handleOpenFAQ = (politica: PoliticaContent) => {
    const faqSlug = generateSlug(politica.title);
    navigate(`/perguntasfrequentes/${faqSlug}`, { replace: false });
    setReadingPolitica(politica);
  };

  const handleCloseFAQ = () => {
    setReadingPolitica(null);
    navigate("/perguntasfrequentes", { replace: true });
  };

  const getShareUrl = (politica: PoliticaContent) => {
    const faqSlug = generateSlug(politica.title);
    return `${window.location.origin}/perguntasfrequentes/live/${faqSlug}`;
  };

  const handleStartEditing = () => {
    if (politicas) {
      setLocalPoliticas([...politicas]);
      setOriginalIds(politicas.map((p) => p.id));
      setIsEditing(true);
    }
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    if (politicas) {
      setLocalPoliticas([...politicas]);
    }
  };

  const handleSaveChanges = () => {
    savePoliticas({
      updates: localPoliticas,
      originalIds,
    });
    setIsEditing(false);
  };

  const handleEditPolitica = (politica: PoliticaContent) => {
    setEditingPolitica(politica);
  };

  const handleSavePolitica = (updatedPolitica: PoliticaContent) => {
    setLocalPoliticas((prev) =>
      prev.map((p) => (p.id === updatedPolitica.id ? updatedPolitica : p)),
    );
  };

  const handleAddPolitica = (newPolitica: Omit<PoliticaContent, "id">) => {
    const id = crypto.randomUUID();
    // Ensure new politicas are visible by default
    setLocalPoliticas((prev) => [
      ...prev,
      {
        ...newPolitica,
        id,
        is_visible: true,
      } as PoliticaContent,
    ]);
  };

  const handleDeletePolitica = (id: string) => {
    setLocalPoliticas((prev) => prev.filter((p) => p.id !== id));
    setDeleteConfirm(null);
  };

  const handleToggleVisibility = (id: string) => {
    setLocalPoliticas((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              is_visible: !p.is_visible,
            }
          : p,
      ),
    );
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <p>Carregando...</p>
      </div>
    );
  }

  const nextOrderIndex =
    localPoliticas.length > 0
      ? Math.max(...localPoliticas.map((p) => p.order_index)) + 1
      : 1;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Perguntas Frequentes - FAQ
          </h1>
          <p className="text-muted-foreground mt-2">
            Perguntas frequentes e informações úteis
          </p>
        </div>

        <div className="flex gap-2 items-center">
          <Button onClick={openCloudHumansChat}>
            <MessageSquare className="mr-2 h-4 w-4" />
            Abrir chamado
          </Button>

          {canEditFAQ && (
            <>
              {!isEditing ? (
                <Button onClick={handleStartEditing} variant="outline">
                  <Edit className="mr-2 h-4 w-4" />
                  Editar Perguntas Frequentes
                </Button>
              ) : (
                <>
                  <Button onClick={handleCancelEditing} variant="outline">
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveChanges} disabled={isSaving}>
                    <Save className="mr-2 h-4 w-4" />
                    {isSaving ? "Salvando..." : "Salvar Alterações"}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search Bar and Filter */}
      <Card className="mb-4 md:mb-6 w-full overflow-hidden bg-primary-50">
        <CardContent className="p-3 md:p-4">
          <div className="flex gap-3 items-end justify-between">
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Busca
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por palavra-chave"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 h-9"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => setSearchQuery("")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex gap-3 items-end">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Conteúdos
                </label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue placeholder="Filtrar por tipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {availableTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Visualização
                </label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="importance">Padrão</SelectItem>
                    <SelectItem value="recent">Mais recente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add button for editing mode */}
      {isEditing && (
        <div className="flex justify-end">
          <Button
            onClick={() => setShowAddDialog(true)}
            variant="outline"
            size="sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar Seção
          </Button>
        </div>
      )}

      {/* Empty state for search */}
      {filteredPoliticas.length === 0 && searchQuery && (
        <div className="py-12 text-center text-muted-foreground">
          <Search className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-lg">
            Nenhum resultado encontrado para "{searchQuery}"
          </p>
        </div>
      )}

      {/* Cards Grid */}
      <div className="space-y-3">
        {paginatedPoliticas.map((politica) => (
          <div
            key={politica.id}
            onClick={!isEditing ? () => handleOpenFAQ(politica) : undefined}
            className={`
              group relative bg-card rounded-xl p-5  shadow-md
              transition-all duration-200 ease-out
              ${
                !isEditing
                  ? "cursor-pointer hover:bg-muted/40 hover:shadow-lg hover:-translate-y-0.5"
                  : "border border-border/50"
              }
            `}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0 space-y-2">
                {/* Type badge - subtle pill */}
                <div className="flex items-center gap-2 flex-wrap">
                  {politica.type && (
                    <Badge size="small" variant="default">
                      {politica.type}
                    </Badge>
                  )}
                  {!politica.is_visible && canEditFAQ && (
                    <span className="text-[10px] bg-destructive/10 text-destructive px-2 py-0.5 rounded-full font-medium uppercase tracking-wide">
                      Oculto
                    </span>
                  )}
                </div>

                {/* Title as main element */}
                <h3 className="text-base font-medium text-foreground leading-snug pr-4">
                  {politica.title}
                </h3>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* {!isEditing && (
                  <span className="text-muted-foreground/0 group-hover:text-muted-foreground text-sm transition-colors flex items-center gap-1.5">
                    <span className="hidden sm:inline">Ler</span>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                )} */}

                {isEditing && (
                  <>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleVisibility(politica.id);
                      }}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={
                        politica.is_visible
                          ? "Ocultar de usuários"
                          : "Mostrar para usuários"
                      }
                    >
                      {politica.is_visible ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                          <line x1="2" x2="22" y1="2" y2="22" />
                        </svg>
                      )}
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditPolitica(politica);
                      }}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(politica.id);
                      }}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
            // Show first page, last page, current page, and pages around current
            const showPage =
              page === 1 ||
              page === totalPages ||
              Math.abs(page - currentPage) <= 1;
            const showEllipsisBefore =
              page === currentPage - 2 && currentPage > 3;
            const showEllipsisAfter =
              page === currentPage + 2 && currentPage < totalPages - 2;

            if (showEllipsisBefore || showEllipsisAfter) {
              return (
                <span key={page} className="text-muted-foreground px-1">
                  ...
                </span>
              );
            }

            if (!showPage) return null;

            return (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "ghost"}
                size="sm"
                onClick={() => setCurrentPage(page)}
                className="h-8 w-8 p-0"
              >
                {page}
              </Button>
            );
          })}

          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* FAQ Read Modal */}
      <FAQReadModal
        politica={readingPolitica}
        open={!!readingPolitica}
        onClose={handleCloseFAQ}
        shareUrl={readingPolitica ? getShareUrl(readingPolitica) : ""}
      />

      <EditPerguntaFrequenteDialog
        politica={editingPolitica}
        open={!!editingPolitica}
        onClose={() => setEditingPolitica(null)}
        onSave={handleSavePolitica}
      />

      <AddPerguntaFrequenteDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAddPolitica}
        nextOrderIndex={nextOrderIndex}
      />

      <AlertDialog
        open={!!deleteConfirm}
        onOpenChange={() => setDeleteConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta seção? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteConfirm && handleDeletePolitica(deleteConfirm)
              }
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PerguntasFrequentes;
